import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Card,
  Button,
  Space,
  Descriptions,
  Tag,
  message,
  Popconfirm,
  Alert,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  PrinterOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import apiService from '../../services/api';
import { formatDate } from '../../utils/helpers';

const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchJobAndDrivers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const jobRes = await apiService.jobs.getById(id);
      setJob(jobRes.data);
    } catch (err) {
      console.error('Error fetching job details:', err);
      setError('Failed to load job details');
      message.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchJobAndDrivers();
  }, [fetchJobAndDrivers]);

  const handleDelete = async () => {
    try {
      await apiService.jobs.delete(id);
      message.success('Job deleted successfully');
      navigate('/jobs');
    } catch (err) {
      console.error('Job deletion error:', err);
      message.error('Failed to delete job');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'assigned':
        return 'processing';
      case 'cancelled':
        return 'error';
      case 'unassigned':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return <Card loading={true} />;
  }

  if (!job) {
    return (
      <Alert
        message="Job Not Found"
        description="The job you're looking for doesn't exist or has been removed."
        type="error"
        showIcon
        action={
          <Button type="primary" onClick={() => navigate('/jobs')}>
            Back to Jobs
          </Button>
        }
      />
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card
        title={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/jobs')}
            >
              Back
            </Button>
            <span>Job Details: {job.reference}</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<PrinterOutlined />}
              onClick={handlePrint}
            >
              Print
            </Button>
            <Button
              icon={<CopyOutlined />}
              onClick={() => navigate(`/jobs/create?clone=${job._id}`)}
            >
              Clone
            </Button>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/jobs/edit/${job._id}`)}
            >
              Edit
            </Button>
            <Popconfirm
              title="Are you sure you want to delete this job?"
              onConfirm={handleDelete}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
              >
                Delete
              </Button>
            </Popconfirm>
          </Space>
        }
      >
        <Descriptions bordered>
          <Descriptions.Item label="Reference" span={3}>
            {job.reference}
          </Descriptions.Item>
          <Descriptions.Item label="Status" span={3}>
            <Tag color={getStatusColor(job.jobStatus)}>
              {job.jobStatus?.toUpperCase()}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Date" span={3}>
            {formatDate(job.date)}
          </Descriptions.Item>
          <Descriptions.Item label="PL Position" span={3}>
            {job.plPosition?.toUpperCase()}
          </Descriptions.Item>
          <Descriptions.Item label="External Reference" span={3}>
            {job.bookingReference || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Related Job" span={3}>
            {job.relatedJob ? (
              <Link to={`/jobs/${job.relatedJob._id}`}>
                {job.relatedJob.bookingReference || job.relatedJob.reference || 'No Reference'}
              </Link>
            ) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Customer" span={3}>
            {job.customer?.name}
          </Descriptions.Item>
          <Descriptions.Item label="Collection Address" span={3}>
            {job.collectionDetails?.address}
          </Descriptions.Item>
          <Descriptions.Item label="Collection Postcode" span={3}>
            {job.collectionDetails?.postcode}
          </Descriptions.Item>
          <Descriptions.Item label="Collection Date" span={3}>
            {formatDate(job.collectionDetails?.date)}
          </Descriptions.Item>
          <Descriptions.Item label="Collection Time" span={3}>
            {job.collectionDetails?.time}
          </Descriptions.Item>
          <Descriptions.Item label="Delivery Address" span={3}>
            {job.delivery?.address}
          </Descriptions.Item>
          <Descriptions.Item label="Delivery Postcode" span={3}>
            {job.delivery?.postcode}
          </Descriptions.Item>
          <Descriptions.Item label="Delivery Date" span={3}>
            {formatDate(job.delivery?.date)}
          </Descriptions.Item>
          <Descriptions.Item label="Delivery Time" span={3}>
            {job.delivery?.time}
          </Descriptions.Item>
        </Descriptions>

        {job.payment && (
          <Card title="Payment Details" style={{ marginTop: 24 }}>
            <Descriptions bordered>
              <Descriptions.Item label="Payment Reference" span={3}>
                <Link to={`/payments/${job.payment._id}`}>
                  {job.payment.reference}
                </Link>
              </Descriptions.Item>
              <Descriptions.Item label="Payment Status" span={3}>
                <Tag color={getStatusColor(job.payment.status)}>
                  {job.payment.status?.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Amount" span={3}>
                £{job.payment.amount?.toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="VAT Rate" span={3}>
                {job.payment.vatRate}%
              </Descriptions.Item>
              <Descriptions.Item label="VAT Amount" span={3}>
                £{job.payment.vatAmount?.toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Total Amount" span={3}>
                £{job.payment.totalAmount?.toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Payment Method" span={3}>
                {job.payment.paymentMethod?.replace('_', ' ').toUpperCase()}
              </Descriptions.Item>
              <Descriptions.Item label="Due Date" span={3}>
                {formatDate(job.payment.dueDate, 'DD/MM/YYYY', true)}
              </Descriptions.Item>
              {job.payment.notes && (
                <Descriptions.Item label="Payment Notes" span={3}>
                  {job.payment.notes}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        )}
      </Card>
    </div>
  );
};

export default JobDetail;